// ─────────────────────────────────────────────
//  DÉTECTION DE MARQUE — affiche le vrai logo (favicon officiel) d'un
//  service reconnu à la place de l'illustration générique par type,
//  quand c'est pertinent (Site Web, Application, E-mail, Sauvegarde Cloud).
//
//  Fonctionnement :
//   1. On cherche d'abord un mot-clé de marque connue dans le NOM saisi
//      (fonctionne même sans URL — "Gmail perso" affiche le logo Gmail).
//   2. Sinon, on extrait le domaine de l'URL ou de l'host renseigné.
//   3. Si un domaine est trouvé, on affiche son favicon réel via le
//      service favicon de Google (aucune clé requise, aucune install).
//      Sinon, l'appelant garde son illustration générique par type —
//      aucun appel externe n'est fait tant que rien n'est détecté.
//
//  Vie privée : le domaine part vers Google (service public de favicons)
//  uniquement quand une marque est reconnue ou qu'une URL est renseignée.
//  Rien n'est envoyé pour un credential sans nom/URL exploitable (ex :
//  Serveur, NAS, Switch — voir BRAND_AWARE_TYPES).
// ─────────────────────────────────────────────

/** Marques courantes reconnues par mot-clé dans le nom du credential.
 *  Ajoutez une entrée ici pour qu'un nom comme "Gmail support" ou
 *  "compte AWS prod" affiche directement le bon logo, même sans URL. */
const KNOWN_BRANDS = [
  { keywords: ["gmail", "google workspace", "g suite", "gsuite"], domain: "gmail.com" },
  { keywords: ["google drive", "drive google"], domain: "drive.google.com" },
  { keywords: ["google cloud", "gcp"], domain: "cloud.google.com" },
  { keywords: ["google"], domain: "google.com" },
  { keywords: ["microsoft 365", "office 365", "o365", "outlook", "exchange"], domain: "outlook.com" },
  { keywords: ["onedrive"], domain: "onedrive.live.com" },
  { keywords: ["azure"], domain: "azure.microsoft.com" },
  { keywords: ["sharepoint"], domain: "sharepoint.com" },
  { keywords: ["microsoft teams", "teams"], domain: "teams.microsoft.com" },
  { keywords: ["microsoft"], domain: "microsoft.com" },
  { keywords: ["github"], domain: "github.com" },
  { keywords: ["gitlab"], domain: "gitlab.com" },
  { keywords: ["bitbucket"], domain: "bitbucket.org" },
  { keywords: ["dropbox"], domain: "dropbox.com" },
  { keywords: ["aws", "amazon web services"], domain: "aws.amazon.com" },
  { keywords: ["cloudflare"], domain: "cloudflare.com" },
  { keywords: ["slack"], domain: "slack.com" },
  { keywords: ["zoom"], domain: "zoom.us" },
  { keywords: ["facebook"], domain: "facebook.com" },
  { keywords: ["instagram"], domain: "instagram.com" },
  { keywords: ["linkedin"], domain: "linkedin.com" },
  { keywords: ["twitter"], domain: "x.com" },
  { keywords: ["wordpress"], domain: "wordpress.com" },
  { keywords: ["shopify"], domain: "shopify.com" },
  { keywords: ["paypal"], domain: "paypal.com" },
  { keywords: ["stripe"], domain: "stripe.com" },
  { keywords: ["netlify"], domain: "netlify.com" },
  { keywords: ["vercel"], domain: "vercel.com" },
  { keywords: ["docker"], domain: "docker.com" },
  { keywords: ["synology"], domain: "synology.com" },
  { keywords: ["qnap"], domain: "qnap.com" },
  { keywords: ["ubiquiti", "unifi"], domain: "ui.com" },
  { keywords: ["cisco"], domain: "cisco.com" },
  { keywords: ["fortinet", "fortigate"], domain: "fortinet.com" },
  { keywords: ["sophos"], domain: "sophos.com" },
  { keywords: ["sonicwall"], domain: "sonicwall.com" },
  { keywords: ["bitwarden"], domain: "bitwarden.com" },
  { keywords: ["lastpass"], domain: "lastpass.com" },
  { keywords: ["1password"], domain: "1password.com" },
  { keywords: ["atera"], domain: "atera.com" },
  { keywords: ["teamviewer"], domain: "teamviewer.com" },
  { keywords: ["anydesk"], domain: "anydesk.com" },
  { keywords: ["adobe"], domain: "adobe.com" },
  { keywords: ["salesforce"], domain: "salesforce.com" },
  { keywords: ["hubspot"], domain: "hubspot.com" },
  { keywords: ["notion"], domain: "notion.so" },
  { keywords: ["trello"], domain: "trello.com" },
  { keywords: ["asana"], domain: "asana.com" },
  { keywords: ["confluence"], domain: "atlassian.com" },
  { keywords: ["jira"], domain: "atlassian.com" },
  { keywords: ["zendesk"], domain: "zendesk.com" },
  { keywords: ["freshdesk"], domain: "freshdesk.com" },
  { keywords: ["mailchimp"], domain: "mailchimp.com" },
  { keywords: ["sendgrid"], domain: "sendgrid.com" },
  { keywords: ["twilio"], domain: "twilio.com" },
  { keywords: ["digitalocean"], domain: "digitalocean.com" },
  { keywords: ["heroku"], domain: "heroku.com" },
  { keywords: ["namecheap"], domain: "namecheap.com" },
  { keywords: ["godaddy"], domain: "godaddy.com" },
  { keywords: ["ovh"], domain: "ovh.com" },
  { keywords: ["cpanel"], domain: "cpanel.net" },
  { keywords: ["plesk"], domain: "plesk.com" },
  { keywords: ["proxmox"], domain: "proxmox.com" },
  { keywords: ["vmware"], domain: "vmware.com" },
  { keywords: ["veeam"], domain: "veeam.com" },
  { keywords: ["backblaze"], domain: "backblaze.com" },
  { keywords: ["wasabi"], domain: "wasabi.com" },
];

/** Types pour lesquels la détection de marque a du sens (un vrai service
 *  en ligne) — le matériel (Serveur, NAS, Routeur, Switch, Imprimante,
 *  SSH, VPN, WiFi, IP_PHONE, PBX) garde toujours son illustration
 *  générique par type, jamais de logo de marque. */
export const BRAND_AWARE_TYPES = new Set(["WEBSITE", "APPLICATION", "EMAIL", "CLOUD_BACKUP"]);

/** Extrait un nom de domaine exploitable d'une URL ou d'un host —
 *  retourne null pour une IP, un nom de machine local, ou une valeur vide. */
function extractDomain(input) {
  if (!input) return null;
  let v = String(input).trim().toLowerCase();
  if (!v) return null;
  v = v.replace(/^[a-z]+:\/\//, "").replace(/^www\./, "");
  v = v.split("/")[0].split(":")[0].split("?")[0];
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(v)) return null; // IP — pas de favicon utile
  if (!v.includes(".")) return null; // nom de machine local (SRV-01, NAS-BUREAU…)
  return v;
}

/**
 * Détermine le domaine à utiliser pour le vrai logo d'un credential :
 * mot-clé de marque connue dans le nom, sinon domaine de l'URL/host.
 * Retourne null si rien n'est exploitable.
 */
export function resolveBrandDomain(name, url, host) {
  const n = (name || "").toLowerCase();
  if (n) {
    for (const brand of KNOWN_BRANDS) {
      if (brand.keywords.some((k) => n.includes(k))) return brand.domain;
    }
  }
  return extractDomain(url) || extractDomain(host);
}

/** URL du favicon réel d'un domaine (service public Google, sans clé). */
export function faviconSrc(domain, size = 64) {
  return `https://www.google.com/s2/favicons?sz=${size}&domain=${encodeURIComponent(domain)}`;
}